import React from "react";
import { Icon } from "../icon/Icon.jsx";
import { Avatar } from "../display/Avatar.jsx";
import { IconButton } from "../buttons/IconButton.jsx";
import { Tag } from "../display/Tag.jsx";

/* Ringover CallLogRow — one entry in the call-logs list.
   Direction glyph + number/line + contact + timestamp + duration stats +
   recording playback + row actions. Tags render on a second line. */

const DIRECTION = {
  incoming: { icon: "phone-incoming", color: "var(--color-call-in)" },
  outgoing: { icon: "phone-outgoing", color: "var(--color-call-out)" },
  missed: { icon: "phone-missed", color: "var(--color-call-missed)" },
  voicemail: { icon: "voicemail", color: "var(--color-call-voicemail)" },
};

function Waveform({ playing }) {
  const bars = [10, 16, 7, 13, 18, 9, 14, 6, 12, 17, 8, 15, 10, 5, 13, 9, 16, 7];
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 2, height: 20 }}>
      {bars.map((h, i) => (
        <span key={i} style={{
          width: 2, height: h, borderRadius: 1,
          background: playing && i < 6 ? "var(--color-brand)" : "var(--rg-grey-700)",
        }} />
      ))}
    </div>
  );
}

export function CallLogRow({
  direction = "incoming",
  flag,
  number,
  line,
  contact = {},
  timestamp,
  stats = [],
  tags = [],
  hasRecording = false,
  style,
  ...rest
}) {
  const [hover, setHover] = React.useState(false);
  const [playing, setPlaying] = React.useState(false);
  const dir = DIRECTION[direction] || DIRECTION.incoming;

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex", flexDirection: "column", gap: 8,
        padding: "12px 16px", borderRadius: "var(--radius-md)",
        background: hover ? "var(--rg-white-a68)" : "transparent",
        boxShadow: hover ? "var(--shadow-glass)" : "none",
        transition: "background var(--duration-base) var(--ease-standard)",
        ...style,
      }}
      {...rest}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {/* direction */}
        <span style={{ color: dir.color, display: "inline-flex", flexShrink: 0 }}>
          <Icon name={dir.icon} size={22} />
        </span>

        {/* number + line */}
        <div style={{ width: 190, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {flag && <span style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", height: 16 }}>{flag}</span>}
            <span style={{ fontSize: "var(--text-sm-size)", fontWeight: "var(--weight-medium)", color: "var(--text-primary)" }}>{number}</span>
          </div>
          {line && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2, color: "var(--text-muted)" }}>
              <Icon name="phone" size={13} />
              <span style={{ fontSize: "var(--text-xs-size)" }}>{line}</span>
            </div>
          )}
        </div>

        {/* contact */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, flex: 1, minWidth: 0 }}>
          {(contact.name || contact.avatar) && <Avatar src={contact.avatar} name={contact.name} size={36} status={contact.status} />}
          <span style={{ fontSize: "var(--text-sm-size)", fontWeight: "var(--weight-medium)", color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{contact.name}</span>
        </div>

        {/* timestamp */}
        <div style={{ width: 130, flexShrink: 0, fontSize: "var(--text-sm-size)", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{timestamp}</div>

        {/* stats */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, width: 72, flexShrink: 0 }}>
          {stats.map((st, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 4, color: st.tone || "var(--text-muted)", fontSize: "var(--text-xs-size)", fontWeight: "var(--weight-medium)" }}>
              <Icon name={st.icon} size={14} />{st.label}
            </span>
          ))}
        </div>

        {/* recording + actions */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          {hasRecording ? (
            <>
              <IconButton icon={playing ? "pause" : "play"} variant="ghost" size="sm" aria-label="Play recording" onClick={() => setPlaying((p) => !p)} />
              <Waveform playing={playing} />
              <IconButton icon="sliders-horizontal" variant="ghost" size="sm" aria-label="Recording settings" />
            </>
          ) : <span style={{ width: 120 }} />}
          <IconButton icon="more-horizontal" variant="ghost" size="sm" aria-label="More actions" style={{ opacity: hover ? 1 : 0.5 }} />
        </div>
      </div>

      {tags.length > 0 && (
        <div style={{ display: "flex", gap: 8, paddingLeft: 38 }}>
          {tags.map((t, i) => <Tag key={i} tone={t.tone}>{t.label}</Tag>)}
        </div>
      )}
    </div>
  );
}

export default CallLogRow;
